import type { ReviewTrustAlert, ReviewTrustOperatorAction } from "./types.ts";

export interface SourceRiskAcknowledgementInput {
  reason: string;
}


export function isBlockingReviewAlert(alert: ReviewTrustAlert): boolean {
  if (alert.severity) return alert.severity === "blocker";
  return alert.kind === "source" || alert.kind === "run";
}

export function getBlockingReviewAlerts(
  alerts: ReviewTrustAlert[] | null | undefined,
): ReviewTrustAlert[] {
  if (!alerts || alerts.length === 0) return [];
  return alerts.filter(isBlockingReviewAlert);
}

export function hasBlockingSourceRisk(
  alerts: ReviewTrustAlert[] | null | undefined,
): boolean {
  return getBlockingReviewAlerts(alerts).length > 0;
}

export function getBlockingReviewAlertDetails(
  alerts: ReviewTrustAlert[] | null | undefined,
): string[] {
  return getBlockingReviewAlerts(alerts).map((alert) => alert.detail);
}

export function reviewTrustExplanation(alert: ReviewTrustAlert): string {
  const explanation = typeof alert.explanation === "string" ? alert.explanation.trim() : "";
  return explanation || alert.detail;
}

export function reviewTrustOperatorAction(alert: ReviewTrustAlert): ReviewTrustOperatorAction {
  if (alert.operator_action) return alert.operator_action;
  if (alert.severity === "info") return "ignore";
  if (alert.kind === "source" || alert.kind === "run") return "resolve_upstream";
  if (alert.kind === "synthesis" && alert.severity === "blocker") return "resolve_upstream";
  return "dismiss";
}

export function reviewTrustOperatorActionText(alert: ReviewTrustAlert): string {
  switch (reviewTrustOperatorAction(alert)) {
    case "resolve_upstream":
      return "Open the related run or source notes, then fix the missing evidence before trusting or publishing this draft.";
    case "dismiss":
      return "You can dismiss this after review if you accept the remaining risk or context gap.";
    case "ignore":
    default:
      return "No action needed; keep this as context while you review the rest of the queue.";
  }
}

export function isDismissibleReviewAlert(alert: ReviewTrustAlert): boolean {
  return reviewTrustOperatorAction(alert) !== "resolve_upstream";
}

export function reviewTrustDismissKey(alert: ReviewTrustAlert): string {
  return [
    alert.id,
    alert.code ?? "",
    alert.severity ?? "",
    alert.run_id ?? "",
    alert.finding_id ?? "",
    reviewTrustExplanation(alert),
  ].join("|");
}
